// import external libraries
import { Controller, useFormContext, useWatch } from 'react-hook-form';

// import components
import {
  GridCompact6,
  GridCompact12
} from 'components/shared/Grid/Grid.components';
import BacklogEmployeeFilter from '../BacklogEmployeeFilter';
import BacklogFunctionFilter from '../BacklogFunctionFilter';
import BacklogPlanUnitFilter from '../BacklogPlanUnitFilter';

// import hooks
import { usePickedPlanUnits } from '../BacklogPlanUnitFilter/BacklogPlanUnitFilter.hooks';

// import styles
import { BacklogFilterFieldBox } from '../../../../Backlog.styles';

// import types
import type { BacklogPlanUnitId } from '../../../../Backlog.types';
import type { BacklogPlanUnitOption } from '../BacklogPlanUnitFilter/BacklogPlanUnitFilter.types';
import type { BacklogFiltersForm } from '../../BacklogFilters.types';
import type { BacklogEmployeeViewFieldsProps } from './BacklogEmployeeViewFields.types';

/**
 * The employee view's own three filters: the plan units it is scoped to, and the employees and functions of
 * those plan units.
 *
 * They live together because they depend on each other. Both lists are read from the plan units, so they are
 * empty until one is picked, and dropping a plan unit takes the picks made from it with it. Keeping the three
 * in one component is also what keeps the plan units out of the other views: resolving the remembered ids
 * asks the app for its plan units, and a patient view has no business making that request.
 */
const BacklogEmployeeViewFields = ({
  startDate,
  endDate
}: BacklogEmployeeViewFieldsProps) => {
  'use memo';

  const { control, setValue } = useFormContext<BacklogFiltersForm>();
  // The lists follow the plan units as they are picked rather than waiting for Toepassen. useWatch rather
  // than watch(): it subscribes properly, where a watch() call in a 'use memo' component can be memoised
  // into a stale value.
  const pickedPlanUnitIds = useWatch({ control, name: 'planUnitIds' });
  // The remembered ids as the plan units themselves — for the field to show, and for the cost centres the
  // two lists are asked for. Resolved once here rather than in each field.
  const { planUnits: pickedPlanUnits } = usePickedPlanUnits(pickedPlanUnitIds);

  /**
   * A plan unit change, with the picks that hang off it kept honest: dropping one clears the employees and
   * functions, because both lists were read from the plan units and which pick came from which is not
   * something those lists say — so a pick from the plan unit just removed would sit there filtering on a
   * plan unit that is no longer selected. Adding a plan unit invalidates nothing, so those picks stay.
   */
  const handlePlanUnitsChange =
    (onChange: (pickedIds: BacklogPlanUnitId[]) => void) =>
    (picked: BacklogPlanUnitOption[]) => {
      const pickedIds = picked.map(({ uniqId }) => uniqId);
      const droppedOne = pickedPlanUnitIds.some(
        (uniqId) => !pickedIds.includes(uniqId)
      );

      if (droppedOne) {
        setValue('employees', []);
        setValue('employeeFunctions', []);
      }

      onChange(pickedIds);
    };

  return (
    <>
      <GridCompact12>
        <BacklogFilterFieldBox>
          <Controller
            name="planUnitIds"
            control={control}
            render={({ field }) => (
              <BacklogPlanUnitFilter
                selected={pickedPlanUnits}
                onChange={handlePlanUnitsChange(field.onChange)}
                // The employee view is scoped by its plan unit: without one the takenlijst would ask for
                // every employee in the tenant, and the two lists below have nothing to offer either. So the
                // field says it is needed rather than letting the planner apply an empty filter and find an
                // empty page.
                required
              />
            )}
          />
        </BacklogFilterFieldBox>
      </GridCompact12>
      <GridCompact6>
        <BacklogFilterFieldBox>
          <Controller
            name="employees"
            control={control}
            render={({ field }) => (
              <BacklogEmployeeFilter
                planUnits={pickedPlanUnits}
                startDate={startDate}
                endDate={endDate}
                selected={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </BacklogFilterFieldBox>
      </GridCompact6>
      <GridCompact6>
        <BacklogFilterFieldBox>
          <Controller
            name="employeeFunctions"
            control={control}
            render={({ field }) => (
              <BacklogFunctionFilter
                planUnits={pickedPlanUnits}
                startDate={startDate}
                endDate={endDate}
                selected={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </BacklogFilterFieldBox>
      </GridCompact6>
    </>
  );
};

export default BacklogEmployeeViewFields;
